export type SubscriptionStatus = "active" | "past-due" | "scheduled"

export type SubscriptionProduct = {
  id: string
  name: string
  amountCents: number
}

export type Subscription = {
  id: string
  name: string
  startDate: string
  endDate: string
  nextInvoiceDate: string
  products: SubscriptionProduct[]
  monthlyAmountCents: number
  status: SubscriptionStatus
}

export type InvoiceStatus = "paid" | "due" | "overdue" | "processing"

export type Invoice = {
  id: string
  subscriptionId: string
  periodLabel: string
  issueDate: string
  dueDate: string
  amountCents: number
  status: InvoiceStatus
}

export enum PaymentMethodTypeEnum {
  Card = "card",
  Ach = "ach",
}

export type PaymentMethodType = `${PaymentMethodTypeEnum}`

export type BankAccountType = "checking" | "savings"

export type CardPaymentMethod = {
  id: string
  type: "card"
  brand: string
  last4: string
  expMonth: string
  expYear: string
  billingName: string
  billingZip: string
  isDefault: boolean
}

export type AchPaymentMethod = {
  id: string
  type: "ach"
  bankName: string
  accountType: BankAccountType
  accountLast4: string
  routingLast4: string
  billingName: string
  billingZip: string
  isDefault: boolean
}

export type PaymentMethod = CardPaymentMethod | AchPaymentMethod

export const autoPayPolicyText =
  "AutoPay is required for all subscriptions. Outstanding invoices are charged immediately when a payment method is added."

export const subscriptions: Subscription[] = [
  {
    id: "easy-cmmc",
    name: "EasyCMMC",
    startDate: "2025-05-02",
    endDate: "2027-05-01",
    nextInvoiceDate: "2026-05-01",
    products: [
      {
        id: "easy-cmmc-core-platform",
        name: "Core Platform",
        amountCents: 99900,
      },
      {
        id: "easy-cmmc-compliance-reporting",
        name: "Compliance Reporting",
        amountCents: 50000,
      },
    ],
    monthlyAmountCents: 149900,
    status: "active",
  },
]

export const invoices: Invoice[] = [
  {
    id: "INV-10420",
    subscriptionId: "easy-cmmc",
    periodLabel: "Apr 2026",
    issueDate: "2026-04-01",
    dueDate: "2026-04-16",
    amountCents: 149900,
    status: "due",
  },
  {
    id: "INV-10421",
    subscriptionId: "easy-cmmc",
    periodLabel: "Apr 2026",
    issueDate: "2026-04-01",
    dueDate: "2026-04-16",
    amountCents: 25000,
    status: "overdue",
  },
  {
    id: "INV-10422",
    subscriptionId: "easy-cmmc",
    periodLabel: "Apr 2026",
    issueDate: "2026-04-01",
    dueDate: "2026-04-16",
    amountCents: 10000,
    status: "due",
  },
  {
    id: "INV-10340",
    subscriptionId: "easy-cmmc",
    periodLabel: "Mar 2026",
    issueDate: "2026-03-01",
    dueDate: "2026-03-16",
    amountCents: 149900,
    status: "paid",
  },
  {
    id: "INV-10341",
    subscriptionId: "easy-cmmc",
    periodLabel: "Mar 2026",
    issueDate: "2026-03-01",
    dueDate: "2026-03-16",
    amountCents: 25000,
    status: "paid",
  },
  {
    id: "INV-10342",
    subscriptionId: "easy-cmmc",
    periodLabel: "Mar 2026",
    issueDate: "2026-03-01",
    dueDate: "2026-03-16",
    amountCents: 10000,
    status: "processing",
  },
]

export const initialPaymentMethods: PaymentMethod[] = [
  {
    id: "pm-visa-1228",
    type: "card",
    brand: "Visa",
    last4: "1228",
    expMonth: "10",
    expYear: "2028",
    billingName: "Sam Kleist",
    billingZip: "98104",
    isDefault: true,
  },
  {
    id: "pm-ach-9921",
    type: "ach",
    bankName: "CloudFit Federal Credit Union",
    accountType: "checking",
    accountLast4: "9921",
    routingLast4: "5432",
    billingName: "Sam Kleist",
    billingZip: "98104",
    isDefault: false,
  },
]

export function formatCurrency(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

export function formatDate(dateText: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(dateText))
}

export function getSubscriptionById(subscriptionId: string) {
  return subscriptions.find((subscription) => subscription.id === subscriptionId)
}

export function getOutstandingInvoices(allInvoices: Invoice[]) {
  return allInvoices.filter((invoice) => invoice.status === "due" || invoice.status === "overdue")
}

export function getTotalAmountCents(items: Array<{ amountCents: number }>) {
  return items.reduce((total, item) => total + item.amountCents, 0)
}
